import NumberTextField from "../../../components/common/NumberTextField";
import * as flagActions from "../../../store/actions/input/flags/flagsActions";
import * as flagSelectors from "../../../store/selectors/input/flags/flagsSelectors";
import * as utils from "../../../utils";
import Component from "../../Component";
import {
  InfoOutlined as InfoIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";
import {
  Autocomplete,
  Checkbox,
  Stack,
  Tooltip,
  TextField,
  TableRow,
  TableHead,
  TableContainer,
  TableCell,
  TableBody,
  Table,
} from "@mui/material";
import IconButton from "components/common/IconButton";
import Notes from "components/common/Notes";
import * as labels from "config.labels";
import React from "react";
import { connect } from "react-redux";
import * as staticDataService from "services/staticDataService";
import * as staticDataHooks from "store/hooks/staticDataHooks";

export const getGoodBad = (valueStr) => {
  if (valueStr == null) {
    return "";
  }
  const value = parseFloat(valueStr);
  if (isNaN(value)) {
    return "";
  }
  if (value === 0) {
    return "";
  }
  return value < 0 ? "good" : "bad";
};

export const UWJudgmentComponent = ({
  judgments,
  judgmentFactorPretty,
  resolvedFlags,
  updateJudgment,
  createJudgment,
  deleteJudgment,
  updateIgnoreOverrides,
}) => {
  const translateLabel = staticDataHooks.useTranslator("uiLabels");

  // Get autocomplete options from static data config
  const { data: judgmentsConfig } = staticDataService.useConfigQuery(
    "judgments"
  );
  const autocompleteOptions = judgmentsConfig?.autocompleteJudgments || [];

  // Check if autocomplete feature is enabled via visibility tag
  const { isVisible: isAutocompleteEnabled } = staticDataHooks.useIsVisible(
    "input.flags.autocompleteDefaultJudgments"
  );

  const handleJudgmentChange = (row, index, column, value) => {
    if (index === judgments.length - 1) {
      const newJudgment = { ...row, [column]: value };
      createJudgment(newJudgment);
    } else {
      updateJudgment(row.id, column, value);
    }
  };

  const createJudgmentError = (
    judgmentStr,
    discount,
    penalty,
    ignoreOverrides
  ) => {
    if (
      judgmentStr == null ||
      discount == null ||
      penalty == null ||
      ignoreOverrides
    ) {
      return "";
    }
    const judgment = parseFloat(judgmentStr);
    if (isNaN(judgment)) {
      return "";
    }
    if (judgment > penalty) {
      return `Exceeds max penalty of ${penalty}%`;
    }
    if (judgment < -discount) {
      return `Exceeds max discount of -${discount}%`;
    }
    return "";
  };

  // Render the label field - use Autocomplete if feature is enabled and options are available
  const renderLabelField = (row, index, judgmentError, disabled) => {
    const hasAutocompleteOptions =
      isAutocompleteEnabled && autocompleteOptions?.length > 0;

    if (hasAutocompleteOptions && !disabled) {
      return (
        <Autocomplete
          value={row.name || ""}
          onChange={(_, newValue) => {
            handleJudgmentChange(row, index, "name", newValue || "");
          }}
          onInputChange={(_, newInputValue, reason) => {
            if (reason === "input") {
              handleJudgmentChange(row, index, "name", newInputValue);
            }
          }}
          options={autocompleteOptions}
          freeSolo
          autoHighlight
          renderInput={(params) => (
            <TextField
              {...params}
              size={"small"}
              variant={"filled"}
              hiddenLabel
              fullWidth
              helperText={
                judgmentError?.length > 0 && (
                  <span
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 6,
                      top: "8px",
                      marginBottom: "10px",
                      position: "relative",
                    }}
                  >
                    <InfoIcon fontSize={"small"} color={"warning"} />
                    <span>{judgmentError}</span>
                  </span>
                )
              }
            />
          )}
          fullWidth
        />
      );
    }

    // Fallback to regular TextField
    return (
      <TextField
        value={row.name}
        onChange={(e) =>
          handleJudgmentChange(row, index, "name", e.target.value)
        }
        size={"small"}
        variant={"filled"}
        autoComplete={"off"}
        hiddenLabel
        fullWidth
        disabled={disabled}
        helperText={
          judgmentError?.length > 0 && (
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                top: "8px",
                marginBottom: "10px",
                position: "relative",
              }}
            >
              <InfoIcon fontSize={"small"} color={"warning"} />
              <span>{judgmentError}</span>
            </span>
          )
        }
      />
    );
  };

  return (
    <Component
      title={translateLabel(labels.UW_JUDGMENT_TITLE, {
        default: "UW Judgment",
      })}
      subtitle={translateLabel(labels.UW_JUDGMENT_SUBTITLE, {
        default:
          "Risk assessments and their estimated impact on the deal. These numbers are reflected in the UW Adjusted number on the Layer Pricing screen.",
      })}
    >
      <TableContainer>
        <Table size={"small"} className={"flags"}>
          <TableHead>
            <TableRow>
              <TableCell width={"5%"} sx={{ minWidth: "30px" }} />
              <TableCell width={"25%"}>
                {translateLabel(labels.UW_JUDGMENT_LABEL_COLUMN, {
                  default: "Label",
                })}
              </TableCell>
              <Tooltip
                title={translateLabel(labels.UW_JUDGMENT_IMPACT_TOOLTIP, {
                  default:
                    "Positive numbers are bad (increase the required premium). Negative numbers are good (decrease the required premium).",
                })}
              >
                <TableCell
                  width={"10%"}
                  sx={{ minWidth: "100px" }}
                  align={"right"}
                >
                  {translateLabel(labels.UW_JUDGMENT_IMPACT_COLUMN, {
                    default: "% Impact",
                  })}
                </TableCell>
              </Tooltip>
              {resolvedFlags?.judgmentLimits && (
                <Tooltip
                  title={translateLabel(labels.UW_JUDGMENT_APPLIED_TOOLTIP, {
                    default:
                      "The applied penalty / discount after constraints are applied.",
                  })}
                >
                  <TableCell
                    width={"10%"}
                    sx={{ minWidth: "100px" }}
                    align={"right"}
                  >
                    {translateLabel(labels.UW_JUDGMENT_APPIED_IMPACT_COLUMN, {
                      default: "% Applied",
                    })}
                  </TableCell>
                </Tooltip>
              )}
              <TableCell width={"70%"}>
                {translateLabel(labels.UW_JUDGMENT_COMMENTS_COLUMN, {
                  default: "Comments",
                })}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {judgments?.map((row, index) => {
              const judgmentError = createJudgmentError(
                row.value,
                resolvedFlags?.judgmentLimits?.maxDiscount,
                resolvedFlags?.judgmentLimits?.maxPenalty,
                resolvedFlags?.judgmentLimits?.ignoreOverrides
              );
              const verticalAlign =
                judgmentError?.length > 0 ? "top" : "center";
              const isLabelDisabled = !(row.canEditName ?? true);

              return (
                <TableRow key={row.id}>
                  <TableCell sx={{ verticalAlign }}>
                    {row.canDelete && index !== judgments?.length - 1 && (
                      <IconButton
                        icon={DeleteIcon}
                        onClick={() => deleteJudgment(row.id)}
                      />
                    )}
                  </TableCell>
                  <TableCell
                    sx={{ verticalAlign }}
                    className={"cell-condensed-padding-md"}
                  >
                    {renderLabelField(
                      row,
                      index,
                      judgmentError,
                      isLabelDisabled
                    )}
                  </TableCell>
                  <TableCell
                    sx={{ verticalAlign }}
                    className={"cell-condensed-padding-md"}
                  >
                    <div>
                      <NumberTextField
                        value={row.value}
                        onChange={(value) => {
                          handleJudgmentChange(row, index, "value", value);
                        }}
                        size={"small"}
                        variant={"filled"}
                        autoComplete={"off"}
                        hiddenLabel
                        fullWidth
                        className={getGoodBad(row.value)}
                        inputProps={{ sx: { textAlign: "right" } }}
                        disabled={!row.canEdit}
                      />
                    </div>
                  </TableCell>
                  {resolvedFlags?.judgmentLimits && (
                    <TableCell
                      sx={{ verticalAlign }}
                      className={"cell-condensed-padding-md"}
                    >
                      <NumberTextField
                        value={resolvedFlags?.judgments?.[index]?.value ?? ""}
                        size={"small"}
                        variant={"filled"}
                        hiddenLabel
                        fullWidth
                        className={getGoodBad(
                          resolvedFlags?.judgments?.[index]?.value
                        )}
                        inputProps={{ sx: { textAlign: "right" } }}
                        disabled
                      />
                    </TableCell>
                  )}
                  <TableCell
                    sx={{ verticalAlign }}
                    className={"cell-condensed-padding-md"}
                  >
                    <Notes
                      key={`${row.id}-${row.comments}`}
                      title={null}
                      hasEditButton={false}
                      update={(comment) =>
                        handleJudgmentChange(row, index, "comments", comment)
                      }
                      value={row.comments}
                      disabled={!row.canEdit}
                    />
                  </TableCell>
                </TableRow>
              );
            })}
            <TableRow>
              <TableCell />
              <TableCell
                style={{ verticalAlign: "center" }}
                className={"cell-condensed-padding-md"}
                align={"right"}
              >
                <Stack justifyContent={"flex-end"}>
                  <b>
                    {translateLabel(labels.UW_JUDGMENT_OVERALL_LABEL, {
                      default: "Overall",
                    })}
                  </b>
                  {resolvedFlags?.judgmentFactorWarning && (
                    <Stack
                      direction={"row"}
                      alignItems={"center"}
                      spacing={1}
                      justifyContent={"flex-end"}
                    >
                      <InfoIcon fontSize={"small"} color={"warning"} />
                      <span
                        style={{
                          fontSize: "12px",
                        }}
                      >
                        {resolvedFlags?.judgmentFactorWarning}
                      </span>
                    </Stack>
                  )}
                </Stack>
              </TableCell>
              <TableCell
                style={{ verticalAlign: "center", pointerEvents: "none" }}
                className={"cell-condensed-padding-md"}
              >
                <TextField
                  value={utils.sfString(judgmentFactorPretty)}
                  size={"small"}
                  variant={"filled"}
                  autoComplete={"off"}
                  hiddenLabel
                  fullWidth
                  disabled
                  className={getGoodBad(judgmentFactorPretty)}
                  inputProps={{
                    sx: { textAlign: "right", fontWeight: "bold" },
                  }}
                />
              </TableCell>
              {resolvedFlags?.judgmentLimits && (
                <>
                  <TableCell
                    style={{ verticalAlign: "center", pointerEvents: "none" }}
                    className={"cell-condensed-padding-md"}
                  >
                    <TextField
                      value={utils.sfString(resolvedFlags.judgmentFactor)}
                      size={"small"}
                      variant={"filled"}
                      autoComplete={"off"}
                      hiddenLabel
                      fullWidth
                      disabled
                      className={getGoodBad(resolvedFlags.judgmentFactor)}
                      inputProps={{
                        sx: { textAlign: "right", fontWeight: "bold" },
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <Stack direction={"row"} sx={{ alignItems: "center" }}>
                      <Checkbox
                        value={resolvedFlags.ignoreOverrides}
                        onChange={(e) => {
                          updateIgnoreOverrides(e?.target?.checked);
                        }}
                      />
                      <div>{"Override max penalty / discount"}</div>
                    </Stack>
                  </TableCell>
                </>
              )}
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    </Component>
  );
};

const mapDispatchToProps = {
  updateJudgment: flagActions.updateJudgment,
  deleteJudgment: flagActions.deleteJudgment,
  createJudgment: flagActions.createJudgment,
  updateIgnoreOverrides: flagActions.updateIgnoreOverrides,
};

const mapStateToProps = (state) => {
  return {
    judgments: flagSelectors.getJudgments(state),
    judgmentFactorPretty: flagSelectors.getJudgmentFactorPretty(state),
    resolvedFlags: flagSelectors.selectResolvedFlags(state),
  };
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(UWJudgmentComponent);
